<template>
  <ion-page
    ref="ionPageRef"
    :swipe-gesture="disableSwipeBack"
  >
    <ion-header
      :translucent="floatingHeader || translucent === false ? false : true"
      class="ion-no-border"
      :class="floatingHeader ? 'toolbar-overlay absolute' : ''"
    >
      <ion-toolbar :data-color="headerColor || 'primary'">
        <ion-buttons slot="start">
          <slot name="actions-start" />
          <BackButtonFloating
            v-if="! hideBackButton && floatingHeader"
            :default-href="pageDefaultBackLink"
          />
          <ion-back-button
            v-if="! hideBackButton && ! floatingHeader"
            :text="''"
            :default-href="pageDefaultBackLink"
          />
        </ion-buttons>
        <!-- eslint-disable vue/no-v-text-v-html-on-component -->
        <ion-title
          v-if="! floatingHeader"
          v-html="pageTitle"
        />
        <ion-buttons slot="end">
          <slot name="actions-end" />
        </ion-buttons>
      </ion-toolbar>
    </ion-header>
    <ion-content
      ref="contentRef"
      :scroll-events="scrollEvents"
      @ionScroll="logScrolling"
    >
      <slot />
    </ion-content>
  </ion-page>
</template>

<script setup lang="ts">
import {
  IonBackButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonPage,
  IonTitle,
  IonToolbar,
} from '@ionic/vue'
import useSessions from '@/assets/ts/useSessions'
import BackButtonFloating from '@/components/BackButtonFloating.vue'

const ionPageRef = ref<InstanceType<typeof IonPage> | null>( null )

defineExpose( {
  ionPageRef,
} )

interface Props {
  /**
   * Whether we should disable the swipe back feature on the page.
   */
  disableSwipeBack?: boolean;
  /**
   * The title of the page.
   */
  pageTitle?: string;
  /**
   * The default back link for the back button.
   */
  pageDefaultBackLink?: string;

  /**
   * The color of the header.
   */
  headerColor?: string;
  /**
   * Whether to hide the back button.
   */
  hideBackButton?: boolean;
  /**
   * Whether to make the header floating.
   *
   * @default false
   */
  floatingHeader?: boolean;
  /**
   * Whether to enable scroll events.
   *
   * @link https://ionicframework.com/docs/api/content#scrollevents
   * @default false
   */
  scrollEvents?: boolean;

  /**
   * Boolean to trigger a scroll to the top of the page.
   */
  scrollToTop?: boolean;

  /**
   * Whether the header should be translucent.
   *
   * @default true
   */
  translucent?: boolean;
}

const props = withDefaults( defineProps<Props>(), {
  floatingHeader: false,
  scrollEvents: false,
  disableSwipeBack: false,
  translucent: true,
} )

const { logScrolling } = useSessions()

const contentRef = ref<InstanceType<typeof IonContent>|null>( null )
const scrollDuration = ref( 500 )

watch( () => props.scrollToTop, ( scrollToTop ) => {
  if ( scrollToTop && contentRef.value ) {
    contentRef.value.$el.scrollToTop( scrollDuration.value )
  }
} )
</script>

<style scoped>
.toolbar-overlay ion-toolbar {
  --background: transparent !important;
}

ion-toolbar[data-color="charcoal"] {
  --background: var(--gray-secondary) !important;
}

.toolbar-overlay .header-background {
  display: none;
}
</style>